'use client';

/**
 * StarRating — 5-star picker for ConversationRatingCard. Radiogroup
 * semantics (one true "checked" value, roving tabindex) rather than 5
 * independent toggle buttons, because a star rating is exactly a radio
 * group's shape: exactly one value selected out of a fixed set, arrow keys
 * move the selection. Hover previews the WOULD-BE rating without committing
 * it (mouse leaving the group falls back to the actual `value`).
 */
import { useRef, useState } from 'react';
import { StarIcon } from './star-icon';

const STAR_COUNT = 5;
const STAR_SIZE = 30;

interface StarRatingProps {
  /** 0 = no rating chosen yet. */
  value: number;
  onChange: (value: number) => void;
  label?: string;
}

export function StarRating({
  value,
  onChange,
  label = 'Rate this chat',
}: StarRatingProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const starRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const display = hovered ?? value;

  const moveSelection = (from: number, delta: number) => {
    const base = from || (delta > 0 ? 0 : 1);
    const next = Math.min(STAR_COUNT, Math.max(1, base + delta));
    onChange(next);
    starRefs.current[next - 1]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex items-center gap-[6px]"
      onMouseLeave={() => setHovered(null)}
    >
      {Array.from({ length: STAR_COUNT }, (_, i) => i + 1).map((n) => {
        const filled = n <= display;
        // Roving tabindex: the checked star is tabbable; if none is checked
        // yet, the first star is (so Tab can reach the group at all).
        const tabbable = value === 0 ? n === 1 : n === value;

        return (
          <button
            key={n}
            ref={(el) => {
              starRefs.current[n - 1] = el;
            }}
            type="button"
            role="radio"
            aria-checked={n === value}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            tabIndex={tabbable ? 0 : -1}
            onMouseEnter={() => setHovered(n)}
            onFocus={() => setHovered(n)}
            onBlur={() => setHovered(null)}
            onClick={() => onChange(n)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                e.preventDefault();
                moveSelection(value, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                e.preventDefault();
                moveSelection(value, -1);
              } else if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(n);
              }
            }}
            className="flex items-center justify-center"
          >
            <StarIcon size={STAR_SIZE} filled={filled} />
          </button>
        );
      })}
    </div>
  );
}
